
import { Controller } from "@hotwired/stimulus"


export default class extends Controller {
  static targets = [
    "cardNumber",
    "cvv",
    "expiry",
    "submit",
    "cardNumberError",
    "cvvError",
    "expiryError"
  ]
  static values = {
    transactionId: String,
    redirectUrl: String,
  }

  connect() {
    this.secureFields = new SecureFields()

    const inputStyles = {
      color: getComputedStyle(document.body).getPropertyValue('--maui-color-text-copy'),
      fontSize: getComputedStyle(document.body).getPropertyValue('font-size'),
      fontFamily: getComputedStyle(document.body).getPropertyValue('--maui-font-family-copy'),
      padding: '6px',
      border: getComputedStyle(document.body).getPropertyValue('--maui-border-default'),
      borderRadius: getComputedStyle(document.body).getPropertyValue('--maui-radii-default-radius'),
      boxSizing: 'border-box',
      outline: 'none',
      height: '42px'
    }

    const focusInputStyles = {
      border: `2px solid ${getComputedStyle(document.body).getPropertyValue('--maui-color-brand-primary')}`,
    }

    const cssFromObject = (obj) => Object.entries(obj)
      .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`)
      .join('\n');

    const style = document.createElement('style');
    style.textContent = `
      #${this.expiryTarget.id} {
        ${cssFromObject(inputStyles)}
      }
      #${this.expiryTarget.id}:focus {
        ${cssFromObject(focusInputStyles)}
      }
    `;
    document.head.appendChild(style);

    this.secureFields.init(
      this.transactionIdValue,
      {
        cvv: { placeholderElementId: this.cvvTarget.id },
        cardNumber: { placeholderElementId: this.cardNumberTarget.id }
      },
      {
        styles: {
          '*': inputStyles,
          '*:focus': focusInputStyles,
        },
        debug: false
      }
    )

    this.secureFields.on("success", this.handleSuccess.bind(this))
    this.secureFields.on("error", this.handleError.bind(this))
    this.secureFields.on("change", this.handleChange.bind(this))
    this.secureFields.on("validate", this.handleValidate.bind(this))
  }

  handleSuccess(data) {
    if (data.redirect)
      window.location = data.redirect // this is the 3DS URL
    else
      Turbo.visit(this.redirectUrlValue) // reload
  }

  clearErrors(){
    this.cardNumberErrorTarget.classList.add("hidden")
    this.cvvErrorTarget.classList.add("hidden")
    this.expiryErrorTarget.classList.add("hidden")
  }

  handleError(data) {
    switch(data.error) {
      case "cardNumber":
        this.cardNumberErrorTarget.classList.remove("hidden")
        break;
      case "cvv":
        this.cvvErrorTarget.classList.remove("hidden")
        break;
      case "Invalid expiry date":
        this.expiryErrorTarget.classList.remove("hidden")
        break;
      default: // declined, Internal error etc
        Turbo.visit(this.redirectUrlValue)
    }
  }

  handleValidate(data) {
    if(!data.fields.cardNumber.valid)
      this.handleError({error: "cardNumber"})
    if(!data.fields.cvv.valid)
      this.handleError({error: "cvv"})
    // expiry date is not validated here but in error callback
  }

  handleChange(data) {
    if (data.event?.type !== "autocomplete") return

    switch (data.event.field) {
      case "expiryMonth":
        this.month = this.parseMonth(data.event.value);
        break;
      case "expiryYear":
        this.year = this.parseYear(data.event.value);
        break;
    }

    this.updateExpiryInput()
  }

  parseExpiry(value) {
    const digits = value.replace(/\D/g, "")
    const [m, y] = digits.length === 4
      ? [digits.slice(0, 2), digits.slice(2)]
      : value.split(/\D/)
    this.month = this.parseMonth(m || "")
    this.year = this.parseYear(y || "")
  }

  parseMonth(value) {
    const v = value.replace(/\D/g, "").slice(-2)
    if (!v) return ""
    const num = parseInt(v, 10)
    if (num < 1 || num > 12) return ""
    return num.toString().padStart(2, "0")
  }

  parseYear(value) {
    const v = value.replace(/\D/g, "").slice(-2)
    if (!v) return ""
    const num = parseInt(v, 10)
    const currentYear = new Date().getFullYear() % 100
    if (num < currentYear || num > currentYear + 10) return ""
    return v.padStart(2, "0")
  }

  blurExpiry(event) {
    this.parseExpiry(event.target.value)
    this.updateExpiryInput()
  }

  updateExpiryInput() {
    this.expiryTarget.value = this.month || this.year ? `${this.month}/${this.year}` : "";
  }

  submit() {
    this.clearErrors()
    this.secureFields.submit({
      expm: this.month,
      expy: this.year
    })
  }
}
